import React, { useEffect, useState } from 'react';
import api from '../../api/api';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchProducts } from '../../store/slices/productSlice';
import { fetchSemiFinished } from '../../store/slices/semiFinishedSlice';
import { fetchDishes } from '../../store/slices/dishSlice';
import { Dish, SemiFinishedProduct, Product } from '../../types';
import { InventoryAdjustment } from '../../types';
import { escapeHtml, getInventoryPrintStyles, getWeightTypeLabel } from '../../utils/inventoryPrint';
import { getSelectableProducts } from '../../utils/selectableProducts';
import { openAndPrintHtml } from '../../utils/printDocument';
import InventoryWarehouseSelect from '../../components/InventoryWarehouseSelect';
import './InventoryNutritionOpsPage.css';

type NutritionRow = { kind: 'product' | 'semi' | 'dish'; productId: string; semiFinishedProductId: string; dishId: string; quantity: string };
type NutritionMetaItem = {
  productId?: number | null;
  semiFinishedProductId?: number | null;
  dishId?: number | null;
  quantity: number;
  expandedProducts?: { productId: number; quantity: number }[];
};
type NutritionPrintRow = {
  id: string;
  selected: boolean;
  date: string;
  reason: string;
  itemName: string;
  unit: string;
  quantity: number;
  sourceIds?: string[];
  aggregationKey?: string;
};

const normalizeMetaItem = (x: any): NutritionMetaItem => ({
  productId: x.ProductId ?? x.productId ?? null,
  semiFinishedProductId: x.SemiFinishedProductId ?? x.semiFinishedProductId ?? null,
  dishId: x.DishId ?? x.dishId ?? null,
  quantity: x.Quantity ?? x.quantity ?? 0,
  expandedProducts: (x.ExpandedProducts ?? x.expandedProducts ?? []).map((p: any) => ({
    productId: p.ProductId ?? p.productId,
    quantity: p.Quantity ?? p.quantity,
  })),
});

const PRINT_STYLE_STORAGE_KEY = 'crm4chef.nutrition.print-style.v1';
type PrintOrientation = 'portrait' | 'landscape';
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const loadPrintStyle = (): { fontSize: number; cellPadding: number; orientation: PrintOrientation } => {
  try {
    const raw = JSON.parse(localStorage.getItem(PRINT_STYLE_STORAGE_KEY) || '{}');
    return {
      fontSize: clamp(Number(raw?.fontSize) || 12, 10, 20),
      cellPadding: clamp(Number(raw?.cellPadding) || 6, 2, 16),
      orientation: raw?.orientation === 'landscape' ? 'landscape' : 'portrait',
    };
  } catch {
    return { fontSize: 12, cellPadding: 6, orientation: 'portrait' };
  }
};
const thStyle: React.CSSProperties = { padding: '8px', textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--crm-table-row-border)' };
type ButtonTone = 'success' | 'info' | 'danger' | 'warning' | 'muted' | 'primary' | 'accent';
const btn = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'primary' ? 'var(--crm-navbar-dropdown-bg)' : tone === 'muted' ? 'var(--crm-surface-alt)' : `var(--crm-${tone === 'accent' ? 'accent' : `${tone}-bg`})`,
  color: tone === 'primary' ? 'var(--crm-primary-contrast)' : tone === 'muted' ? 'var(--crm-text-muted)' : tone === 'accent' ? 'var(--crm-accent-contrast)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'primary' || tone === 'muted' ? 'border' : tone === 'accent' ? 'accent' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  cursor: 'pointer',
  padding: '8px 12px',
  ...extra,
});

type PickerTab = 'dish' | 'semi' | 'product';

// Groups items by category string
function groupByCategory<T extends { categoryName?: string | null }>(items: T[]): { cat: string; items: T[] }[] {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const cat = item.categoryName || 'Без категорії';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat)!.push(item);
  }
  return Array.from(map.entries()).map(([cat, items]) => ({ cat, items }));
}

// --- Picker Modal ---
const NutritionPickerModal: React.FC<{
  dishes: Dish[];
  semiFinished: SemiFinishedProduct[];
  products: Product[];
  onAdd: (kind: NutritionRow['kind'], id: string, qty?: string) => void;
  onClose: () => void;
}> = ({ dishes, semiFinished, products, onAdd, onClose }) => {
  const [tab, setTab] = useState<PickerTab>('dish');
  const [search, setSearch] = useState('');
  const q = search.toLowerCase();

  const filteredDishes = q ? dishes.filter((d) => d.name.toLowerCase().includes(q)) : dishes;
  const filteredSemi = q ? semiFinished.filter((s) => s.name.toLowerCase().includes(q)) : semiFinished;
  const filteredProducts = q ? products.filter((p) => p.name.toLowerCase().includes(q)) : products;

  const dishGroups = groupByCategory(filteredDishes);
  const semiGroups = groupByCategory(filteredSemi);
  const productGroups = groupByCategory(filteredProducts);

  const tabBtn = (t: PickerTab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === t}
      aria-controls="nutrition-picker-results"
      onClick={() => { setTab(t); setSearch(''); }}
      className={`crm-picker-tab ${tab === t ? 'crm-picker-tab--active' : ''}`}
    >
      {label}
    </button>
  );

  const itemRow = (kind: PickerTab, id: number, name: string) => (
    <button
      type="button"
      key={id}
      onClick={() => onAdd(kind, String(id), '1')}
      className="crm-picker-item"
    >
      <span>{name}</span>
      <span className="crm-picker-item-accent">+</span>
    </button>
  );

  return (
    <div
      onClick={onClose}
      className="crm-picker-overlay nutrition-picker-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="crm-picker-modal nutrition-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nutrition-picker-title"
      >
        {/* Header */}
        <div className="crm-picker-header">
          <strong id="nutrition-picker-title" style={{ fontSize: '15px' }}>Додати позицію</strong>
          <button type="button" aria-label="Закрити вибір позицій" onClick={onClose} className="nutrition-picker-close">✕</button>
        </div>
        {/* Tabs */}
        <div className="crm-picker-tabs" role="tablist" aria-label="Тип позиції">
          {tabBtn('dish', `🍽 Страви (${filteredDishes.length})`)}
          {tabBtn('semi', `🧪 Н/Ф (${filteredSemi.length})`)}
          {tabBtn('product', `📦 Продукти (${filteredProducts.length})`)}
        </div>
        {/* Search */}
        <div className="crm-picker-search-wrap">
          <input
            autoFocus
            aria-label="Пошук позицій"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Пошук..."
          />
        </div>
        {/* List */}
        <div id="nutrition-picker-results" className="crm-picker-list">
          {tab === 'dish' && (
            dishGroups.length === 0
              ? <div style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Нічого не знайдено</div>
              : dishGroups.map(({ cat, items }) => (
                <div key={cat}>
                  <div className="crm-picker-group-title">{cat}</div>
                  {items.map((d) => itemRow('dish', d.id, d.name))}
                </div>
              ))
          )}
          {tab === 'semi' && (
            semiGroups.length === 0
              ? <div style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Нічого не знайдено</div>
              : semiGroups.map(({ cat, items }) => (
                <div key={cat}>
                  <div className="crm-picker-group-title">{cat}</div>
                  {items.map((s) => itemRow('semi', s.id, s.name))}
                </div>
              ))
          )}
          {tab === 'product' && (
            productGroups.length === 0
              ? <div style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Нічого не знайдено</div>
              : productGroups.map(({ cat, items }) => (
                <div key={cat}>
                  <div className="crm-picker-group-title">{cat}</div>
                  {items.map((p) => itemRow('product', p.id, p.name))}
                </div>
              ))
          )}
        </div>
        <div className="crm-picker-footer">
          <button onClick={onClose} style={btn('muted')}>Готово</button>
        </div>
      </div>
    </div>
  );
};

const InventoryNutritionOpsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { products } = useAppSelector((s) => s.product);
  const { items: semiFinished } = useAppSelector((s) => s.semiFinished);
  const dishes = useAppSelector((s) => s.dish.items);
  const [items, setItems] = useState<InventoryAdjustment[]>([]);
  const [selected, setSelected] = useState<InventoryAdjustment | null>(null);
  const [selectedNutritionIds, setSelectedNutritionIds] = useState<number[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const { fontSize: printFontSize, cellPadding: printCellPadding, orientation: printOrientation } = React.useMemo(() => loadPrintStyle(), []);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [warehouseId, setWarehouseId] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<NutritionRow[]>([]);
  const [modalMetaRow, setModalMetaRow] = useState<NutritionMetaItem | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expandOnPrint, setExpandOnPrint] = useState(false);
  const [printPreview, setPrintPreview] = useState<{ title: string; documentCount: number; aggregateByPosition: boolean; rows: NutritionPrintRow[] } | null>(null);

  const resetForm = () => {
    setDate(new Date().toISOString().slice(0, 10));
    setWarehouseId('');
    setNotes('');
    setRows([]);
    setEditingId(null);
    setModalMetaRow(null);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<InventoryAdjustment[]>('/inventoryadjustments', { params: { operationType: 'Nutrition' } });
      const activeItems = res.data.filter((row) => row.status?.toLowerCase() !== 'cancelled');
      setItems(activeItems);
      setSelectedNutritionIds((prev) => prev.filter((id) => activeItems.some((row) => row.id === id)));
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Не вдалося завантажити операції харчування.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchSemiFinished());
    dispatch(fetchDishes());
    load();
  }, [dispatch]);

  const open = async (id: number) => {
    try {
      const res = await api.get<InventoryAdjustment>(`/inventoryadjustments/${id}`);
      setSelected(res.data);
      setModalMetaRow(null);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Не вдалося завантажити операцію.');
    }
  };

  const parseMetaRows = (reason: string): NutritionRow[] => {
    const toRow = (x: NutritionMetaItem): NutritionRow => ({
      kind: x.dishId ? 'dish' : x.semiFinishedProductId ? 'semi' : 'product',
      productId: x.productId ? String(x.productId) : '',
      semiFinishedProductId: x.semiFinishedProductId ? String(x.semiFinishedProductId) : '',
      dishId: x.dishId ? String(x.dishId) : '',
      quantity: String(x.quantity),
    });
    const parts = (reason || '').split('|');
    if (parts.length < 2) return [];
    try {
      const raw = JSON.parse(parts[1]);
      const rawRows: any[] = Array.isArray(raw) ? raw : (raw?.Rows ?? raw?.rows ?? []);
      const rows = rawRows.map(normalizeMetaItem);
      return rows.filter((x) => x.quantity > 0 && (x.productId || x.semiFinishedProductId || x.dishId)).map(toRow);
    } catch { return []; }
  };

  const parseMetaPayload = (reason: string): NutritionMetaItem[] => {
    const parts = (reason || '').split('|');
    if (parts.length < 2) return [];
    try {
      const raw = JSON.parse(parts[1]);
      const rawRows: any[] = Array.isArray(raw) ? raw : (raw?.Rows ?? raw?.rows ?? []);
      const rows = rawRows.map(normalizeMetaItem);
      return rows.filter((x) => x.quantity > 0 && (x.productId || x.semiFinishedProductId || x.dishId));
    } catch { return []; }
  };

  const startEdit = (item: InventoryAdjustment) => {
    setEditingId(item.id);
    setDate(item.date.slice(0, 10));
    setWarehouseId(item.warehouseId ? String(item.warehouseId) : '');
    setNotes(item.notes || '');
    const metaRows = parseMetaRows(item.reason || '');
    if (metaRows.length) {
      setRows(metaRows);
    } else {
      const mapped = (item.items || []).filter((r) => r.quantityDelta < 0).map((r) => ({ kind: 'product' as const, productId: r.productId ? String(r.productId) : '', semiFinishedProductId: '', dishId: '', quantity: String(Math.abs(r.quantityDelta)) }));
      setRows(mapped.length ? mapped : []);
    }
    setShowForm(true);
  };

  const updateRow = (idx: number, patch: Partial<NutritionRow>) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const addFromPicker = (kind: NutritionRow['kind'], id: string, qty = '1') => {
    setRows((prev) => [...prev, { kind, productId: kind === 'product' ? id : '', semiFinishedProductId: kind === 'semi' ? id : '', dishId: kind === 'dish' ? id : '', quantity: qty }]);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      date: new Date(date).toISOString(),
      warehouseId: warehouseId ? Number(warehouseId) : null,
      notes,
      items: rows
        .map((r) => ({
          productId: r.kind === 'product' ? Number(r.productId) : null,
          semiFinishedProductId: r.kind === 'semi' ? Number(r.semiFinishedProductId) : null,
          dishId: r.kind === 'dish' ? Number(r.dishId) : null,
          quantity: Number(r.quantity),
        }))
        .filter((r) => r.quantity > 0 && ((r.productId ?? 0) > 0 || (r.semiFinishedProductId ?? 0) > 0 || (r.dishId ?? 0) > 0)),
    };
    try {
      const savedEditingId = editingId;
      if (editingId) await api.put(`/inventoryadjustments/${editingId}/nutrition`, payload);
      else await api.post('/inventoryadjustments/nutrition', payload);
      setShowForm(false);
      resetForm();
      await load();
      if (selected && savedEditingId) await open(savedEditingId);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Помилка збереження.');
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Скасувати операцію харчування? Вона залишиться в історії, але більше не впливатиме на склад.')) return;
    const reason = window.prompt('Вкажіть причину скасування:');
    if (!reason?.trim()) return;
    await api.post(`/inventoryadjustments/${id}/cancel`, { reason: reason.trim() });
    if (selected?.id === id) setSelected(null);
    setSelectedNutritionIds((prev) => prev.filter((x) => x !== id));
    await load();
  };

  const postNutrition = async (id: number) => {
    if (!window.confirm('Провести харчування? Після проведення кількості будуть списані зі складу.')) return;
    try {
      await api.post(`/inventoryadjustments/${id}/nutrition/post`);
      await load();
      await open(id);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Не вдалося провести харчування.');
    }
  };

  const toggleNutritionSelection = (id: number) => {
    setSelectedNutritionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAllVisibleNutrition = () => {
    const visibleIds = items.map((x) => x.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedNutritionIds.includes(id));
    if (allSelected) {
      setSelectedNutritionIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedNutritionIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const markSelectedNutritionAsAccounting = async () => {
    if (selectedNutritionIds.length === 0) {
      alert('Оберіть хоча б одну операцію харчування.');
      return;
    }
    await api.put('/inventoryadjustments/accounting-transfer', {
      ids: selectedNutritionIds,
      isTransferredToAccounting: true,
    });
    await load();
    if (selected && selectedNutritionIds.includes(selected.id)) await open(selected.id);
  };

  const productNameById = new Map(products.map((p) => [p.id, p.name]));
  const semiNameById = new Map(semiFinished.map((s) => [s.id, s.name]));
  const dishNameById = new Map(dishes.map((d) => [d.id, d.name]));

  const getMetaTypeName = (row: NutritionMetaItem) => {
    if (row.dishId) return 'Страва';
    if (row.semiFinishedProductId) return 'Н/Ф';
    return 'Продукт';
  };
  const getMetaItemName = (row: NutritionMetaItem) => {
    if (row.dishId) return dishNameById.get(row.dishId) || `Страва #${row.dishId}`;
    if (row.semiFinishedProductId) return semiNameById.get(row.semiFinishedProductId) || `Н/Ф #${row.semiFinishedProductId}`;
    if (row.productId) return productNameById.get(row.productId) || `Продукт #${row.productId}`;
    return '—';
  };
  const getRowDisplayName = (r: NutritionRow) => {
    if (r.kind === 'dish' && r.dishId) return dishNameById.get(Number(r.dishId)) || `Страва #${r.dishId}`;
    if (r.kind === 'semi' && r.semiFinishedProductId) return semiNameById.get(Number(r.semiFinishedProductId)) || `Н/Ф #${r.semiFinishedProductId}`;
    if (r.kind === 'product' && r.productId) return productNameById.get(Number(r.productId)) || `Продукт #${r.productId}`;
    return '—';
  };
  const kindBadge = (kind: NutritionRow['kind']) => {
    const map: Record<NutritionRow['kind'], [string, string]> = {
      dish: ['info', 'Страва'],
      semi: ['warning', 'Н/Ф'],
      product: ['success', 'Продукт'],
    };
    const [tone, label] = map[kind];
    return <span style={{ display: 'inline-block', background: `var(--crm-${tone}-bg)`, color: `var(--crm-${tone})`, border: `1px solid var(--crm-${tone})`, borderRadius: '4px', padding: '1px 6px', fontSize: '11px', fontWeight: 700, marginRight: '4px', whiteSpace: 'nowrap' }}>{label}</span>;
  };

  // Build expanded flat product list for print
  const buildExpandedPrintRows = (metaRows: NutritionMetaItem[]): { productId: number; name: string; quantity: number }[] => {
    const totals = new Map<number, number>();
    for (const row of metaRows) {
      if (row.expandedProducts && row.expandedProducts.length > 0) {
        for (const ep of row.expandedProducts) {
          totals.set(ep.productId, (totals.get(ep.productId) || 0) + ep.quantity);
        }
      } else if (row.productId) {
        totals.set(row.productId, (totals.get(row.productId) || 0) + row.quantity);
      }
    }
    return Array.from(totals.entries()).map(([productId, quantity]) => ({
      productId,
      name: productNameById.get(productId) || `Продукт #${productId}`,
      quantity,
    })).sort((a, b) => a.name.localeCompare(b.name, 'uk'));
  };

  const getPrintRowsForDocument = (doc: InventoryAdjustment): NutritionPrintRow[] => {
    const metaRows = parseMetaPayload(doc.reason || '');
    const reason = (doc.reason || '').split('|')[0].trim() || 'Харчування';
    const dateLabel = new Date(doc.date).toLocaleDateString('uk-UA');

    if (metaRows.length === 0) {
      return (doc.items || []).map((i) => ({
        id: `item-${doc.id}-${i.id}`,
        selected: true,
        date: dateLabel,
        reason,
        itemName: i.productName || '—',
        unit: getWeightTypeLabel(i.weightType),
        quantity: Math.abs(i.quantityDelta || 0),
        aggregationKey: JSON.stringify(['product', i.productId ?? i.productName ?? '', getWeightTypeLabel(i.weightType).toLocaleLowerCase('uk')]),
      }));
    }

    if (expandOnPrint) {
      return buildExpandedPrintRows(metaRows).map((row, index) => ({
        id: `expanded-${doc.id}-${row.productId}-${index}`,
        selected: true,
        date: dateLabel,
        reason,
        itemName: row.name,
        unit: getWeightTypeLabel(products.find((p) => p.id === row.productId)?.weightType || ''),
        quantity: row.quantity,
        aggregationKey: JSON.stringify(['product', row.productId, getWeightTypeLabel(products.find((p) => p.id === row.productId)?.weightType || '').toLocaleLowerCase('uk')]),
      }));
    }

    return metaRows.map((row, index) => {
      const unit = row.dishId
        ? 'порц'
        : row.semiFinishedProductId
          ? getWeightTypeLabel(semiFinished.find((s) => s.id === row.semiFinishedProductId)?.weightType || '')
          : getWeightTypeLabel(row.productId ? products.find((p) => p.id === row.productId)?.weightType : '');
      const identity = row.dishId
        ? ['dish', row.dishId]
        : row.semiFinishedProductId
          ? ['semi', row.semiFinishedProductId]
          : ['product', row.productId ?? getMetaItemName(row)];
      return {
        id: `meta-${doc.id}-${index}`,
        selected: true,
        date: dateLabel,
        reason,
        itemName: getMetaItemName(row),
        unit,
        quantity: row.quantity,
        aggregationKey: JSON.stringify([...identity, unit.toLocaleLowerCase('uk')]),
      };
    });
  };

  const openPrintPreview = async (documents: InventoryAdjustment[], title: string) => {
    const sortedDocs = [...documents].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const rows = sortedDocs.flatMap(getPrintRowsForDocument);
    setPrintPreview({ title, documentCount: sortedDocs.length, aggregateByPosition: false, rows });
  };

  const getPrintPreviewRows = (preview: NonNullable<typeof printPreview>): NutritionPrintRow[] => {
    if (!preview.aggregateByPosition) return preview.rows;

    const groups = new Map<string, NutritionPrintRow[]>();
    for (const row of preview.rows) {
      const key = row.aggregationKey || JSON.stringify(['name', row.itemName.trim().toLocaleLowerCase('uk'), row.unit.trim().toLocaleLowerCase('uk')]);
      const group = groups.get(key);
      if (group) group.push(row);
      else groups.set(key, [row]);
    }

    return Array.from(groups.entries()).map(([key, group]) => ({
      id: `aggregate-${key}`,
      sourceIds: group.flatMap(row => row.sourceIds || [row.id]),
      selected: group.every(row => row.selected),
      date: '',
      reason: '',
      itemName: group[0].itemName,
      unit: group[0].unit,
      quantity: group.reduce((total, row) => total + row.quantity, 0),
    })).sort((a, b) => a.itemName.localeCompare(b.itemName, 'uk'));
  };

  const printRows = (preview: NonNullable<typeof printPreview>) => {
    const rows = getPrintPreviewRows(preview).filter((row) => row.selected);
    const datedRows = preview.aggregateByPosition ? [] : rows;
    const periodFrom = datedRows[0]?.date || '';
    const periodTo = datedRows[datedRows.length - 1]?.date || '';
    const rowsHtml = rows.map((row) => `
      <tr>
        ${preview.aggregateByPosition ? '' : `<td>${escapeHtml(row.date)}</td>`}
        <td>${escapeHtml(row.itemName)}</td>
        <td style="text-align:center;">${escapeHtml(row.unit)}</td>
        <td style="text-align:right;">${row.quantity.toFixed(3)}</td>
      </tr>`).join('');

    void openAndPrintHtml(`<html><head><title>${escapeHtml(preview.title)}</title>
      <style>
        @page { size: A4 ${printOrientation}; margin: 10mm; }
        body{font-family:Arial,sans-serif;padding:14px;color:#2c3e50}
        ${getInventoryPrintStyles()}
        .pdf-receipt-root table { table-layout: fixed; width: 100%; }
        .pdf-receipt-root th, .pdf-receipt-root td { font-size:${printFontSize}px !important; padding:${printCellPadding}px ${printCellPadding + 2}px !important; vertical-align: top; }
        .pdf-receipt-root td { word-break: break-word; overflow-wrap: anywhere; }
        .pdf-receipt-root .col-date { width: 14%; white-space: nowrap; }
        .pdf-receipt-root .col-item { width: ${preview.aggregateByPosition ? '70%' : '56%'}; }
        .pdf-receipt-root .col-unit { width: 10%; text-align:center; white-space: nowrap; }
        .pdf-receipt-root .col-qty { width: 10%; text-align:right; white-space: nowrap; }
      </style>
      </head><body>
      <div class="pdf-receipt-root"><section class="receipt-sheet">
        <div class="receipt-top">
          <div class="receipt-title-row">
            <h2>${escapeHtml(preview.title)}${periodFrom && periodTo ? ` з ${escapeHtml(periodFrom)} по ${escapeHtml(periodTo)}` : preview.aggregateByPosition ? ' — зведено за позиціями' : ''}</h2>
            <span class="page-index">Обрано документів: ${preview.documentCount}</span>
          </div>
          ${expandOnPrint ? '<div style="background:#fff3cd;border:1px solid #ffc107;border-radius:6px;padding:6px 10px;margin-top:6px;font-size:12px;"><strong>⚠ Розгорнуто до продуктів</strong></div>' : ''}
        </div>
        <table>
          <thead><tr>${preview.aggregateByPosition ? '' : '<th class="col-date">Дата</th>'}<th class="col-item">Назва позиції</th><th class="col-unit">Од.</th><th class="col-qty">К-сть</th></tr></thead>
          <tbody>${rowsHtml || `<tr><td colspan="${preview.aggregateByPosition ? 3 : 4}" style="text-align:center;color:#7f8c8d;">Позиції відсутні</td></tr>`}</tbody>
        </table>
      </section></div>
      </body></html>`, { windowFeatures: 'width=1200,height=800' });
  };

  const print = () => {
    if (!selected) return;
    void openPrintPreview([selected], `Харчування #${selected.id}`);
  };

  const printSelectedNutritionSheet = async () => {
    if (selectedNutritionIds.length === 0) {
      alert('Оберіть хоча б одну операцію харчування для друку.');
      return;
    }

    const docsRaw = await Promise.all(
      selectedNutritionIds.map(async (id) => {
        try {
          const res = await api.get<InventoryAdjustment>(`/inventoryadjustments/${id}`);
          return res.data;
        } catch {
          return null;
        }
      })
    );
    const docs = docsRaw.filter((x): x is InventoryAdjustment => x != null);
    if (docs.length === 0) {
      alert('Не вдалося завантажити обрані операції харчування для друку.');
      return;
    }

    void openPrintPreview(docs, 'Харчування за період');
  };

  const visiblePrintRows = printPreview ? getPrintPreviewRows(printPreview) : [];
  const selectedPrintRowCount = visiblePrintRows.filter(row => row.selected).length;

  const selectedMetaRows = selected ? parseMetaPayload(selected.reason || '') : [];
  const hasExpandable = selectedMetaRows.some((r) => (r.dishId || r.semiFinishedProductId) && (r.expandedProducts?.length || 0) > 0);

  const getListPrimaryPosition = (adjustment: InventoryAdjustment) => {
    const rows = parseMetaPayload(adjustment.reason || '');
    if (rows.length === 0) return adjustment.reason || '—';
    const label = getMetaItemName(rows[0]);
    return rows.length > 1 ? `${label} +${rows.length - 1}` : label;
  };

  const calculateNutritionCost = (metaRows: NutritionMetaItem[]): number => {
    let totalCost = 0;
    for (const row of metaRows) {
      if (row.productId) {
        const product = products.find((p) => p.id === row.productId);
        if (product) totalCost += (product.price || 0) * row.quantity;
      } else if (row.semiFinishedProductId) {
        const semi = semiFinished.find((s) => s.id === row.semiFinishedProductId);
        if (semi) totalCost += (semi.costPrice || 0) * row.quantity;
      } else if (row.dishId) {
        const dish = dishes.find((d) => d.id === row.dishId);
        if (dish) totalCost += (dish.costPrice || 0) * row.quantity;
      }
    }
    return totalCost;
  };

  const getNutritionCostForAdjustment = (adjustment: InventoryAdjustment): number => {
    const metaRows = parseMetaPayload(adjustment.reason || '');
    if (metaRows.length > 0) {
      return calculateNutritionCost(metaRows);
    }
    let totalCost = 0;
    for (const item of adjustment.items || []) {
      if (item.productId) {
        const product = products.find((p) => p.id === item.productId);
        if (product) totalCost += (product.price || 0) * Math.abs(item.quantityDelta);
      }
    }
    return totalCost;
  };

  return (
    <div className="page-shell page-shell--wide inventory-nutrition-page" style={{ maxWidth: '1500px' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Харчування</h2>
      <div className="no-print nutrition-layout" style={{ marginTop: '16px' }}>
        <div className="nutrition-sidebar">
          <div style={{ marginBottom: '12px' }}>
            <button onClick={() => { resetForm(); setShowForm(true); }} style={btn('success', { width: '100%' })}>+ Нова операція</button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <button
              type="button"
              disabled={selectedNutritionIds.length === 0}
              onClick={printSelectedNutritionSheet}
              style={btn(selectedNutritionIds.length === 0 ? 'muted' : 'primary', { width: '100%' })}
            >
              🖨 Друк обраних ({selectedNutritionIds.length})
            </button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <button
              type="button"
              disabled={selectedNutritionIds.length === 0}
              onClick={markSelectedNutritionAsAccounting}
              style={btn(selectedNutritionIds.length === 0 ? 'muted' : 'success', { width: '100%' })}
            >
              ✓ Передані в бухгалтерію ({selectedNutritionIds.length})
            </button>
          </div>
          <div className="nutrition-document-list">
            <label className="nutrition-select-all">
              <input type="checkbox" checked={items.length > 0 && items.every((x) => selectedNutritionIds.includes(x.id))} onChange={toggleAllVisibleNutrition} />
              Обрати всі
            </label>
            <ul aria-label="Список списань харчування">
              {items.map((i) => (
                <li key={i.id} className={`nutrition-document${selected?.id === i.id ? ' nutrition-document--selected' : ''}`}>
                  <label className="nutrition-document-check">
                    <input type="checkbox" checked={selectedNutritionIds.includes(i.id)} onChange={() => toggleNutritionSelection(i.id)} aria-label={`Обрати харчування #${i.id}`} />
                  </label>
                  <button type="button" className="nutrition-document-open" onClick={() => open(i.id)} aria-label={`Відкрити харчування #${i.id}`} aria-pressed={selected?.id === i.id}>
                    <span className="nutrition-document-heading">
                      <span>#{i.id} · {new Date(i.date).toLocaleDateString('uk-UA')}</span>
                      <span className={`nutrition-document-status nutrition-document-status--${i.status === 'Draft' ? 'draft' : 'posted'}`}>{i.status === 'Draft' ? 'Чернетка' : 'Проведено'}</span>
                    </span>
                    <span className="nutrition-document-position">{getListPrimaryPosition(i)}</span>
                    <span className="nutrition-document-summary">
                      <span>Бухгалтерія: {i.isTransferredToAccounting ? '✓' : '—'}</span>
                      <span>Списання: {Math.abs(i.totalDelta).toFixed(3)}</span>
                      <strong>Вартість: {getNutritionCostForAdjustment(i).toFixed(2)} ₴</strong>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {items.length === 0 && <p className="nutrition-list-empty">{loading ? 'Завантаження...' : 'Даних немає'}</p>}
          </div>
        </div>

        <div className="nutrition-content">
          {showForm && (
            <form onSubmit={save} className="inventory-receipts-card" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: '16px' }}>
              <h3>{editingId ? 'Редагування чернетки харчування' : 'Нова чернетка харчування'}</h3>
              <p style={{ color: 'var(--crm-warning)', marginTop: 0 }}>Чернетка не впливає на склад. Перевірте дані та проведіть документ після збереження.</p>
              <div className="inventory-receipts-form-grid" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <label>Дата:<input type="date" required value={date} onChange={(e) => setDate(e.target.value)} /></label>
                <InventoryWarehouseSelect value={warehouseId} onChange={setWarehouseId} />
                <label style={{ flex: 1 }}>Примітка:<input value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
              </div>

              {/* Added rows */}
              {rows.length > 0 && (
                <table className="inventory-receipts-items-table nutrition-entry-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: '55%' }}>Позиція</th>
                      <th style={{ ...thStyle, width: '30%' }}>К-сть</th>
                      <th style={{ ...thStyle, width: '15%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={idx}>
                        <td style={tdStyle}>
                          {kindBadge(r.kind)}
                          <span style={{ fontSize: '13px' }}>{getRowDisplayName(r)}</span>
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="number" step="0.001" min="0.001" required
                            aria-label={`Кількість для ${getRowDisplayName(r)}`}
                            value={r.quantity}
                            onChange={(e) => updateRow(idx, { quantity: e.target.value })}
                            style={{ width: '100%', padding: '4px 6px' }}
                          />
                        </td>
                        <td style={tdStyle}>
                        <button type="button" onClick={() => removeRow(idx)} aria-label={`Видалити ${getRowDisplayName(r)}`} style={btn('danger', { padding: '4px 8px' })}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="crm-mobile-mobile-row" style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setPickerOpen(true)} style={btn('accent', { borderRadius: '999px' })}>
                  + Додати позицію
                </button>
                {rows.length === 0 && (
                  <span style={{ color: 'var(--crm-text-muted)', fontSize: '13px' }}>Натисніть "+ Додати позицію" щоб обрати страву, Н/Ф або продукт</span>
                )}
              </div>

              <div className="inventory-receipts-form-actions" style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" style={btn('success', { borderRadius: '999px' })}>Зберегти чернетку</button>
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} style={btn('muted', { borderRadius: '999px' })}>Скасувати</button>
              </div>
            </form>
          )}

          {selected && !showForm && (
            <div className="print-area" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)' }}>
              <div className="inventory-receipts-detail-header crm-mobile-mobile-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <h3>Харчування #{selected.id} {selected.status === 'Cancelled' ? '· СКАСОВАНО' : selected.status === 'Draft' ? '· ЧЕРНЕТКА' : '· ПРОВЕДЕНО'}</h3>
                <div className="no-print inventory-receipts-detail-actions crm-mobile-mobile-row" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {hasExpandable && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: 'var(--crm-accent)', fontWeight: 600 }}>
                      <input type="checkbox" checked={expandOnPrint} onChange={(e) => setExpandOnPrint(e.target.checked)} style={{ cursor: 'pointer' }} />
                      Розгорнути до продуктів при друці
                    </label>
                  )}
                  {selected.status === 'Draft' && <button onClick={() => startEdit(selected)} style={btn('warning')}>Редагувати</button>}
                  {selected.status === 'Draft' && <button onClick={() => postNutrition(selected.id)} style={btn('success')}>Провести харчування</button>}
                  {selected.status !== 'Cancelled' && <button onClick={() => remove(selected.id)} style={btn('danger')}>Скасувати</button>}
                  <button onClick={print} style={btn('muted')}>Друк</button>
                </div>
              </div>
              {selected.status !== 'Draft' && selected.status !== 'Cancelled' && <p style={{ color: 'var(--crm-text-muted)' }}>Проведений документ незмінний. Для виправлення скасуйте його та створіть новий.</p>}
              {selected.status === 'Draft' && <p style={{ color: 'var(--crm-warning)', fontWeight: 600 }}>Чернетка не впливає на склад. Після перевірки натисніть «Провести харчування».</p>}
              {selected.postedAtUtc && <p>Проведено: {new Date(selected.postedAtUtc).toLocaleString('uk-UA')} · Автор: {selected.postedByUsername || 'історичний запис'}</p>}
              <p>Дата: {new Date(selected.date).toLocaleDateString('uk-UA')}</p>
              {selected.notes && <p>Примітка: {selected.notes}</p>}
              <div style={{ background: 'var(--crm-info-bg)', border: '2px solid var(--crm-info)', borderRadius: '6px', padding: '12px', marginBottom: '12px' }}>
                <strong style={{ color: 'var(--crm-text)', fontSize: '14px' }}>Приблизна вартість харчування:</strong>
                <span style={{ marginLeft: '8px', fontSize: '18px', fontWeight: 700, color: 'var(--crm-info)' }}>
                  {getNutritionCostForAdjustment(selected).toFixed(2)} ₴
                </span>
              </div>
              <h4 style={{ margin: '12px 0 6px' }}>Позиції списання</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={thStyle}>Тип</th><th style={thStyle}>Позиція</th><th style={{ ...thStyle, textAlign: 'center' }}>Од.</th><th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th><th style={{ ...thStyle, textAlign: 'right' }}>Вартість</th></tr></thead>
                <tbody>
                  {selectedMetaRows.length > 0 ? selectedMetaRows.map((row, idx) => {
                    const expandable = !!(row.dishId || row.semiFinishedProductId) && (row.expandedProducts?.length || 0) > 0;
                    const unitLabel = row.dishId ? 'порц'
                      : row.semiFinishedProductId ? getWeightTypeLabel(semiFinished.find((s) => s.id === row.semiFinishedProductId)?.weightType || '')
                      : getWeightTypeLabel(row.productId ? products.find((p) => p.id === row.productId)?.weightType : '');
                    let rowCost = 0;
                    if (row.productId) {
                      const product = products.find((p) => p.id === row.productId);
                      rowCost = (product?.price || 0) * row.quantity;
                    } else if (row.semiFinishedProductId) {
                      const semi = semiFinished.find((s) => s.id === row.semiFinishedProductId);
                      rowCost = (semi?.costPrice || 0) * row.quantity;
                    } else if (row.dishId) {
                      const dish = dishes.find((d) => d.id === row.dishId);
                      rowCost = (dish?.costPrice || 0) * row.quantity;
                    }
                    return (
                      <tr key={`meta-${idx}`} onClick={() => { if (expandable) setModalMetaRow(row); }} title={expandable ? 'Натисніть для перегляду складу' : undefined} style={{ cursor: expandable ? 'pointer' : 'default' }}>
                        <td style={tdStyle}>{getMetaTypeName(row)}</td>
                        <td style={{ ...tdStyle, color: expandable ? 'var(--crm-accent)' : undefined }}>{getMetaItemName(row)}{expandable ? ' 🔍' : ''}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{unitLabel || '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.quantity.toFixed(3)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--crm-accent)' }}>{rowCost.toFixed(2)} ₴</td>
                      </tr>
                    );
                  }) : (selected.items || []).map((i) => {
                    const product = products.find((p) => p.id === i.productId);
                    const itemCost = (product?.price || 0) * Math.abs(i.quantityDelta);
                    return (
                      <tr key={i.id}><td style={tdStyle}>Продукт</td><td style={tdStyle}>{i.productName || '—'}</td><td style={{ ...tdStyle, textAlign: 'center' }}>{getWeightTypeLabel(i.weightType)}</td><td style={{ ...tdStyle, textAlign: 'right' }}>{Math.abs(i.quantityDelta).toFixed(3)}</td><td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--crm-accent)' }}>{itemCost.toFixed(2)} ₴</td></tr>
                    );
                  })}
                  {selectedMetaRows.length === 0 && (selected.items || []).length === 0 && (
                    <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center' }}>Немає даних</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {printPreview && (
        <div
          className="inventory-nutrition-print-overlay"
          onClick={() => setPrintPreview(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: '20px' }}
        >
          <div
            className="inventory-nutrition-print-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(1000px, 96vw)', maxHeight: '90vh', overflow: 'auto', background: 'var(--crm-surface)', color: 'var(--crm-text)', borderRadius: '12px', boxShadow: 'var(--crm-shadow-sm)', padding: '18px' }}
          >
            <div className="inventory-nutrition-print-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0 }}>Попередній перегляд друку</h3>
                <div style={{ color: 'var(--crm-text-muted)', fontSize: '13px', marginTop: '4px' }}>
                  Відмітьте позиції, які потрібно надрукувати. Обрано: {selectedPrintRowCount}
                </div>
              </div>
              <button type="button" onClick={() => setPrintPreview(null)} style={btn('muted')}>Закрити</button>
            </div>
            {printPreview.documentCount > 1 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', padding: '10px', border: '1px solid var(--crm-info)', borderRadius: '8px', background: 'var(--crm-info-bg)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={printPreview.aggregateByPosition}
                  onChange={(event) => setPrintPreview((prev) => prev ? { ...prev, aggregateByPosition: event.target.checked } : prev)}
                />
                <span><strong>Підсумувати однакові позиції</strong><br /><small>Не розділяти за днями; скласти кількість позицій з усіх обраних списань.</small></span>
              </label>
            )}
            <div className="inventory-nutrition-print-selection-actions" style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              <button
                type="button"
                onClick={() => setPrintPreview((prev) => prev ? { ...prev, rows: prev.rows.map((row) => ({ ...row, selected: true })) } : prev)}
                style={btn('primary')}
              >
                Обрати всі
              </button>
              <button
                type="button"
                onClick={() => setPrintPreview((prev) => prev ? { ...prev, rows: prev.rows.map((row) => ({ ...row, selected: false })) } : prev)}
                style={btn('muted')}
              >
                Зняти всі
              </button>
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--crm-border)', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>✓</th>
                    {!printPreview.aggregateByPosition && <th style={thStyle}>Дата</th>}
                    <th style={thStyle}>Позиція</th>
                    <th style={thStyle}>Од.</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePrintRows.map((row) => (
                    <tr key={row.id}>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={row.selected}
                          onChange={() => setPrintPreview((prev) => prev ? {
                            ...prev,
                            rows: prev.rows.map((sourceRow) =>
                              (row.sourceIds || [row.id]).includes(sourceRow.id)
                                ? { ...sourceRow, selected: !row.selected }
                                : sourceRow
                            ),
                          } : prev)}
                        />
                      </td>
                      {!printPreview.aggregateByPosition && <td style={tdStyle}>{row.date}</td>}
                      <td style={tdStyle}>{row.itemName}</td>
                      <td style={tdStyle}>{row.unit}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{row.quantity.toFixed(3)}</td>
                    </tr>
                  ))}
                  {selectedPrintRowCount === 0 && (
                    <tr><td colSpan={printPreview.aggregateByPosition ? 4 : 5} style={{ ...tdStyle, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Немає вибраних позицій для друку.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="inventory-nutrition-print-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
              <button type="button" onClick={() => setPrintPreview(null)} style={btn('muted')}>Скасувати</button>
              <button
                type="button"
                disabled={selectedPrintRowCount === 0}
                onClick={() => printRows(printPreview)}
                style={btn(selectedPrintRowCount === 0 ? 'muted' : 'success')}
              >
                🖨 Друкувати обрані позиції
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expand modal */}
      {modalMetaRow && (
        <div className="inventory-nutrition-meta-overlay" onClick={() => setModalMetaRow(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '20px' }}>
          <div className="inventory-nutrition-meta-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(760px, 96vw)', maxHeight: '85vh', overflow: 'auto', background: 'var(--crm-surface)', color: 'var(--crm-text)', borderRadius: '12px', boxShadow: 'var(--crm-shadow-sm)', padding: '16px' }}>
            <div className="inventory-nutrition-meta-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0 }}>{getMetaTypeName(modalMetaRow)}: {getMetaItemName(modalMetaRow)}</h3>
              <button onClick={() => setModalMetaRow(null)} style={btn('muted')}>Закрити</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>Списаний продукт</th><th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th></tr></thead>
              <tbody>
                {(modalMetaRow.expandedProducts || []).map((p) => (
                  <tr key={`modal-${p.productId}`}>
                    <td style={tdStyle}>{productNameById.get(p.productId) || `Продукт #${p.productId}`}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{p.quantity.toFixed(3)}</td>
                  </tr>
                ))}
                {(modalMetaRow.expandedProducts || []).length === 0 && (
                  <tr><td colSpan={2} style={{ ...tdStyle, textAlign: 'center' }}>Немає деталізації</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Picker modal */}
      {pickerOpen && (
        <NutritionPickerModal
          dishes={dishes}
          semiFinished={semiFinished}
          products={getSelectableProducts(products, rows.filter(r => r.kind === 'product').map(r => Number(r.productId)))}
          onAdd={(kind, id, qty) => { addFromPicker(kind, id, qty); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
};

export default InventoryNutritionOpsPage;
